import { useEffect, useState } from "react";
import $ from "jquery";
import apiURL from "../../config/environment";
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
    CircularProgress
} from "@mui/material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { isNumberPositiveInt } from "../../utils";
import CheckIcon from "@mui/icons-material/Check";
import {isAfter} from "date-fns";
import {ScheduleSelect} from "./ScheduleSelect";

export const EditIntersectionDialog = ({
  handleClose,
  existingRows,
  setExistingRows,
  rowToEdit,
    schedules
}) => {
  const [siteId, setSiteId] = useState(rowToEdit.id);
  const [location, setLocation] = useState(rowToEdit.displayName);
  const [detectors, setDetectors] = useState(rowToEdit.detectors);
  const [crossings, setCrossings] = useState(rowToEdit.crossings * 2);
  const [archived, setArchived] = useState(rowToEdit.archived ? true : false);
  const [deviceEffectiveDate, setDeviceEffectiveDate] = useState(new Date(rowToEdit.deviceEffectiveDate));
  const [saving, setSaving] = useState(false);
  const [scheduleId, setScheduleId] = useState(null);

  useEffect(() => {
    if (rowToEdit) {
      setSiteId(rowToEdit.id);
      setLocation(rowToEdit.displayName);
      setDetectors(rowToEdit.detectors);
      setCrossings(rowToEdit.crossings * 2);
      setArchived(rowToEdit.archived);
      setDeviceEffectiveDate(new Date(rowToEdit.deviceEffectiveDate));
      setDateError(null);
        setScheduleId(rowToEdit.scheduleId);
    }
  }, [rowToEdit]);

  const [siteIdError, setSiteIdError] = useState(null);
  const [detectorsError, setDetectorsError] = useState(null);
  const [crossingsError, setCrossingsError] = useState(null);
  const [saveError, setSaveError] = useState(false);
  const [dateError, setDateError] = useState(null);

  const handleDialogClose = () => {
    setSiteIdError(null);
    setDetectorsError(null);
    setCrossingsError(null);
    setSaveError(false);
    handleClose();
  };

  useEffect(() => {
    isNumberPositiveInt(siteId, "Site ID", setSiteIdError);
    isNumberPositiveInt(detectors, "Detectors", setDetectorsError);
    isNumberPositiveInt(crossings, "Pedestrian Buttons", setCrossingsError);
  }, [siteId, detectors, crossings]);

  if (!rowToEdit) return null;

  const handleSave = () => {
    if (
      !isNumberPositiveInt(siteId, "Site ID", setSiteIdError) ||
      !isNumberPositiveInt(detectors, "Detectors", setDetectorsError) ||
      !isNumberPositiveInt(crossings, "Pedestrian Buttons", setCrossingsError)
    ) {
      return;
    }

    if (!existingRows.find((row) => row.id === siteId)) {
      setSiteIdError("Site ID does not exist");
      return;
    }
    setSaving(true);
    const token = JSON.parse(localStorage.getItem("userInfo"))?.token;
    $.ajax({
      method: "PATCH",
      url: apiURL + "admin/site/scats",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      contentType: "application/json",
      data: JSON.stringify({
        id: siteId,
        displayName: location,
        detectors: detectors * 1,
        crossings: crossings / 2,
        archived: archived,
        deviceEffectiveDate: deviceEffectiveDate.toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        scheduleId: scheduleId,
      }),
    })
      .done((data) => {
        setSaving(false);
        const filteredRows = existingRows.filter(
          (row) => row.id !== rowToEdit.id
        );
        setExistingRows([...filteredRows, data]);
        handleDialogClose();
      })
      .fail((jqXHR, textStatus, errorThrows) => {
        setSaving(false);
        console.error(
          "Error adding intersection",
          jqXHR,
          textStatus,
          errorThrows
        );
        setSaveError(true);
      });
  };

  return (
    <Dialog open={true} onClose={() => handleDialogClose()}>
      <Box sx={{ width: "444px", padding: "16px 24px 24px 24px" }}>
        <DialogTitle sx={{ padding: 0 }} id="form-dialog-title">
          Edit Intersection
        </DialogTitle>
        <DialogContent sx={{ padding: 0 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              marginTop: "16px",
            }}
          >
            {saveError && (
              <Alert severity={"error"}>
                Unable to save. Please try again later.
              </Alert>
            )}
            <TextField
              autoFocus
              id="name"
              type="text"
              fullWidth
              label="Site ID"
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              error={siteIdError}
              helperText={siteIdError}
              disabled={true}
            />
            <TextField
              id="id"
              type="text"
              fullWidth
              label="Location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />

            <Box display={"flex"} gap={"16px"}>
              <TextField
                error={detectorsError}
                helperText={detectorsError}
                type="number"
                label="Detectors"
                value={detectors}
                onChange={(e) => setDetectors(e.target.value)}
                fullWidth
              />
              <TextField
                error={crossingsError}
                helperText={crossingsError}
                type="number"
                label="Ped Buttons"
                value={crossings}
                onChange={(e) => setCrossings(e.target.value)}
                fullWidth
                inputProps={{
                  min: 0,
                  step: 2,
                }}
              />
            </Box>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker label="Device Effective Date"
                          value={deviceEffectiveDate}
                          onChange={(nv) => {
                            if (isAfter(nv, new Date())) {
                              setDateError("Device Effective Date cannot be in the future");
                              setDeviceEffectiveDate(nv)
                              return;
                            }
                            setDateError(null);
                            setDeviceEffectiveDate(nv)
                          }}
                          maxDate={new Date()}
                          slotProps={{
                            textField: {
                              helperText: dateError || "",
                            }
                          }}
                          format={"yyyy-MM-dd"}
              />
            </LocalizationProvider>

            <ScheduleSelect
              schedules={schedules}
              value={scheduleId}
                onChange={(e) => {
                    setScheduleId(e.target.value);
                }}
              />
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: 0, marginTop: "16px" }}>
          <Button disabled={saving} onClick={() => handleDialogClose()} color="primary">
            Cancel
          </Button>
          <Button
            onClick={(e) => handleSave(e)}
            disabled={
              !siteId ||
              !location ||
              detectors === undefined ||
              crossings === undefined ||
              siteIdError ||
              detectorsError ||
              crossingsError ||
              dateError ||
              saving
            }
            variant={"contained"}
            sx={{
              backgroundColor: "rgba(19, 151, 203, 1)",
              gap: "8px",
              width: "91.75px",
              height: "36.5px",
            }}
          >
            {saving ? (<CircularProgress size={"24.5px"} />) : (
                <>
                  <CheckIcon sx={{ width: "16px", height: "24px" }} />
                  Save
                </>
              )}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
